"use client";

import {createContext, useCallback, useContext, useMemo, useState, useSyncExternalStore, type ReactNode} from "react";
import type {ApprovalDecisionOutcome} from "@/types/chatMessages";
import type {ToolApprovalResponse} from "@/services/wingman";
import {canSubmitApprovalDecisions, type ToolApproval} from "@/services/wingman/tool-approval";

type PendingDecision = "apply" | "discard" | "fresh";
/**
 * `pending` locks one surface's action on this approval. `draft` holds a decision until every
 * pending approval in the same reply has one; `submitting` locks them all while the reply submits.
 */
type DecisionState = Readonly<{pending: PendingDecision | null; error: boolean; requestedFresh: boolean; draft: boolean | null; submitting: boolean}>;

function createDecisionStore() {
   let state: DecisionState = {pending: null, error: false, requestedFresh: false, draft: null, submitting: false};
   const listeners = new Set<() => void>();
   const update = (patch: Partial<DecisionState>) => {
      state = {...state, ...patch};
      listeners.forEach(listener => listener());
   };
   return {
      getSnapshot: () => state,
      subscribe: (listener: () => void) => {
         listeners.add(listener);
         return () => {listeners.delete(listener);};
      },
      // Acquire synchronously so two mounted surfaces cannot submit the same decision.
      begin: (pending: PendingDecision) => {
         if (state.pending || state.submitting || (pending === "fresh" && state.requestedFresh)) return false;
         update({pending, error: false});
         return true;
      },
      update,
   };
}

type DecisionStore = ReturnType<typeof createDecisionStore>;
const ToolApprovalDecisionContext = createContext<Map<string, DecisionStore> | null>(null);

/** Client-only recovery state survives navigation between chat, dock and script review. */
export function ToolApprovalDecisionProvider({children}: {children: ReactNode}) {
   const [stores] = useState(() => new Map<string, DecisionStore>());
   return <ToolApprovalDecisionContext.Provider value={stores}>{children}</ToolApprovalDecisionContext.Provider>;
}

/** Looks up the shared store for an approval ID, creating it on first use. */
function useToolApprovalDecisionStores(): (approvalId: string) => DecisionStore {
   const shared = useContext(ToolApprovalDecisionContext);
   // Without a provider, a surface still keeps its stores across renders.
   const [local] = useState(() => new Map<string, DecisionStore>());
   const stores = shared ?? local;
   return useCallback((approvalId: string) => {
      let found = stores.get(approvalId);
      if (!found) {
         found = createDecisionStore();
         stores.set(approvalId, found);
      }
      return found;
   }, [stores]);
}

/** Local action commands for one approval; authoritative status still comes from the chat. */
export function useToolApprovalDecisionController(approvalId: string | undefined) {
   const storeFor = useToolApprovalDecisionStores();
   const store = useMemo(() => approvalId ? storeFor(approvalId) : createDecisionStore(), [storeFor, approvalId]);
   const state = useSyncExternalStore(store.subscribe, store.getSnapshot, store.getSnapshot);
   return {
      state,
      begin: store.begin,
      finish: () => store.update({pending: null}),
      fail: () => store.update({error: true}),
      markFreshRequested: () => store.update({requestedFresh: true}),
   };
}

function createDecisionGroupStore(ids: string[], storeFor: (approvalId: string) => DecisionStore) {
   const stores = ids.map(storeFor);
   let snapshot = stores.map(store => store.getSnapshot());
   return {
      ids,
      stores,
      subscribe: (listener: () => void) => {
         const unsubscribes = stores.map(store => store.subscribe(listener));
         return () => unsubscribes.forEach(unsubscribe => unsubscribe());
      },
      getSnapshot: () => {
         const next = stores.map(store => store.getSnapshot());
         if (next.some((state, index) => state !== snapshot[index])) snapshot = next;
         return snapshot;
      },
   };
}

/** Subscribe to every displayed approval, even after its reply stops accepting decisions. */
function useDecisionGroup(approvalIds: string[]) {
   const storeFor = useToolApprovalDecisionStores();
   const key = JSON.stringify(approvalIds);
   // Reuse the subscription when callers recreate the same ID list. JSON preserves
   // arbitrary IDs; cached immutable snapshots keep useSyncExternalStore stable.
   const replyStore = useMemo(() => createDecisionGroupStore(JSON.parse(key), storeFor), [key, storeFor]);
   const state = useSyncExternalStore(replyStore.subscribe, replyStore.getSnapshot, replyStore.getSnapshot);
   return {...replyStore, state};
}

/** Read-only subscription for reviews, including approvals that are no longer actionable. */
export function useToolApprovalDecisionStates(approvalIds: string[]): readonly DecisionState[] {
   return useDecisionGroup(approvalIds).state;
}

/**
 * Wingman settles every pending approval in a reply with one request. Each decision is held
 * as a draft until the last pending approval is decided, then all are submitted together.
 * A failed submission keeps the drafts and rejects, so deciding any approval again retries.
 */
export function useReplyApprovalDecisions({approvals, onSubmit}: {
   approvals: ToolApproval[];
   onSubmit?: (decisions: ToolApprovalResponse[]) => Promise<boolean>;
}) {
   const replyStore = useDecisionGroup(approvals.map(approval => approval.id));
   const {state} = replyStore;
   const undecidedIds = replyStore.ids.filter((_, index) => state[index].draft === null);
   const submitting = state.some(state => state.submitting);

   const decide = async (decisions: ToolApprovalResponse[]): Promise<ApprovalDecisionOutcome> => {
      if (!onSubmit) return "ignored";
      const {ids: pendingIds, stores} = replyStore;
      if (stores.some(store => store.getSnapshot().submitting)) return "ignored";
      const storesByApprovalId = new Map(pendingIds.map((id, index) => [id, stores[index]]));
      const applicableDecisions = decisions.flatMap(decision => {
         const store = storesByApprovalId.get(decision.id);
         return store ? [{decision, store}] : [];
      });
      if (!applicableDecisions.length) return "ignored";
      applicableDecisions.forEach(({decision, store}) => store.update({draft: decision.approved, error: false}));
      const draftedDecisions = pendingIds.flatMap((id, index) => {
         const draft = stores[index].getSnapshot().draft;
         return draft === null ? [] : [{id, approved: draft}];
      });
      if (draftedDecisions.length !== pendingIds.length) return "queued";
      // Lock synchronously so no surface can change or resubmit a decision in flight.
      stores.forEach(store => store.update({submitting: true, error: false}));
      try {
         if (!canSubmitApprovalDecisions(approvals, draftedDecisions, Date.now())) throw new Error("Approval decisions are no longer valid");
         if (!(await onSubmit(draftedDecisions))) throw new Error("Approval submission refused");
         stores.forEach(store => store.update({submitting: false, draft: null}));
         return "submitted";
      } catch (error) {
         stores.forEach(store => store.update({submitting: false, error: true}));
         throw error;
      }
   };

   return {decide, undecidedIds, submitting};
}
